import { IconAdd } from '@consta/icons/IconAdd'
import { IconTrash } from '@consta/icons/IconTrash'
import { Button } from '@consta/uikit/Button'
import { GroupingHeader } from '@core/components'
import { selectActiveEntity, useAppSelector } from '@core/core/store'
import type { BaseEntity } from '@core/core/types'
import { nanoid } from '@reduxjs/toolkit'
import type { FC } from 'react'

import { getHorizonDataItem, getInterpretationDataItem } from '../helpers'
import type { AddedSelectItemType } from '../types'
import { maxSelectItemLength } from './constants'
import css from './MapBuildingRightMenu.css'
import { SelectItem } from './SelectItem'
import type { SelectItemMenuProps } from './types'

export const SelectItemMenu: FC<SelectItemMenuProps> = ({
  addedSelectItemList,
  updateSelectItemList,
  updateSourcesData,
  formData,
}) => {
  const activeEntity = useAppSelector(selectActiveEntity)

  const deleteAllObjects = () => {
    updateSelectItemList([{ id: nanoid(), value: null, color: '' }])
    updateSourcesData(prev => ({
      ...prev,
      interpretations2d: [],
      interpretations3d: [],
      horizons: [],
    }))
  }

  const deleteSelectItem = (item: AddedSelectItemType) => () => {
    updateSelectItemList(objectList => objectList.filter(object => object.id !== item.id))

    removeEntityFromSourceData(item.value?.entityId)
  }

  const createSelectItem = () => {
    updateSelectItemList(objectList => [...objectList, { id: nanoid(), value: null, color: '' }])
  }

  const removeEntityFromSourceData = (entityId?: string) => {
    updateSourcesData(prev => ({
      ...prev,
      interpretations2d: prev.interpretations2d.filter(({ id }) => id !== entityId),
      interpretations3d: prev.interpretations3d.filter(({ id }) => id !== entityId),
      horizons: prev.horizons.filter(({ id }) => id !== entityId),
    }))
  }

  const addSelectItemValue = (item: AddedSelectItemType) => (value: BaseEntity, color?: string) => {
    const prevSelectItemId = item.value?.entityId

    if (prevSelectItemId) {
      removeEntityFromSourceData(prevSelectItemId)
    }

    updateSelectItemList(objectList =>
      objectList.map(object => (object.id === item.id ? { ...object, value, color } : object)),
    )

    const valueEntityId = value.entityId
    if (valueEntityId && activeEntity) {
      updateSourcesData(prev => {
        let newValue = prev
        switch (value.entityType) {
          case 'interpretation2D':
            newValue = {
              ...prev,
              interpretations2d: [
                ...prev.interpretations2d,
                getInterpretationDataItem(valueEntityId, 'H2D', color),
              ],
            }
            break
          case 'interpretation3D':
            newValue = {
              ...prev,
              interpretations3d: [
                ...prev.interpretations3d,
                getInterpretationDataItem(valueEntityId, 'H3D', color),
              ],
            }
            break
          case 'horizon':
            newValue = {
              ...prev,
              horizons: [...prev.horizons, getHorizonDataItem(valueEntityId, color)],
            }
            break
          default:
            break
        }

        return newValue
      })
    }
  }

  const clearSelectItemValue = (item: AddedSelectItemType) => () => {
    updateSelectItemList(selectItemList =>
      selectItemList.map(object =>
        object.id === item.id
          ? {
              ...object,
              value: null,
            }
          : object,
      ),
    )
    updateSourcesData(prev => ({
      ...prev,
      interpretations2d: prev.interpretations2d.filter(
        ({ id: horizonId }) => horizonId !== item.value?.entityId,
      ),
      interpretations3d: prev.interpretations3d.filter(
        ({ id: horizonId }) => horizonId !== item.value?.entityId,
      ),
      horizons: prev.horizons.filter(({ id: horizonId }) => horizonId !== item.value?.entityId),
    }))
  }

  const onChangeHorizonWeight = (horizonId: string, value: number) => {
    const currentHorizon2d = formData.buildInfo.sources.interpretations2d.find(
      ({ id }) => horizonId === id,
    )
    const currentHorizon3d = formData.buildInfo.sources.interpretations3d.find(
      ({ id }) => horizonId === id,
    )
    const currentHorizonId = formData.buildInfo.sources.horizons.find(({ id }) => horizonId === id)

    let itemName: 'interpretations2d' | 'interpretations3d' | 'horizons' = 'interpretations2d'

    if (value <= 1) {
      if (currentHorizon2d) {
        currentHorizon2d.weight = value
        itemName = 'interpretations2d'
      }
      if (currentHorizon3d) {
        currentHorizon3d.weight = value
        itemName = 'interpretations3d'
      }
      if (currentHorizonId) {
        currentHorizonId.weight = value
        itemName = 'horizons'
      }
    }
    updateSourcesData(prev => ({
      ...prev,
      [itemName]: formData.buildInfo.sources[itemName],
    }))
  }
  return (
    <>
      <GroupingHeader className={css.header} title="Интерпретация горизонта">
        <Button
          onlyIcon={true}
          iconLeft={IconAdd}
          view="ghost"
          size="xs"
          onClick={createSelectItem}
        />
        <Button
          size="xs"
          view="ghost"
          iconLeft={IconTrash}
          onlyIcon={true}
          onClick={deleteAllObjects}
        />
      </GroupingHeader>
      {addedSelectItemList.map(
        (item, index) =>
          index !== maxSelectItemLength && (
            <SelectItem
              key={item.id}
              item={item}
              itemsLength={addedSelectItemList.length}
              addSelectItem={addSelectItemValue(item)}
              deleteSelectItem={deleteSelectItem(item)}
              clearSelectItemValue={clearSelectItemValue(item)}
              formData={formData}
              onChangeHorizonWeight={onChangeHorizonWeight}
            />
          ),
      )}
    </>
  )
}
